import React, { createContext, useContext, useState, useEffect } from "react";
import { currencyApi } from "../lib/api";

const CurrencyContext = createContext(null);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
};

// Currency list for UI
export const CURRENCIES = ["UAH", "USD", "EUR"];

export const CurrencyProvider = ({ children }) => {
  const [displayCurrency, setDisplayCurrency] = useState(() => {
    return localStorage.getItem("displayCurrency") || "UAH";
  });
  // Exchange rates: all rates are relative to UAH (base currency)
  const [rates, setRates] = useState({
    USD: 41.5,  // 1 USD = 41.5 UAH
    EUR: 43.5,  // 1 EUR = 43.5 UAH
    UAH: 1
  });
  const [rateDate, setRateDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExchangeRates();
  }, []);

  useEffect(() => {
    localStorage.setItem("displayCurrency", displayCurrency);
  }, [displayCurrency]);

  const fetchExchangeRates = async () => {
    try {
      const response = await currencyApi.getCurrent();
      if (response.data) {
        setRates(prev => ({
          ...prev,
          USD: response.data.usd_to_uah || 41.5,
          EUR: response.data.eur_to_uah || 43.5
        }));
        setRateDate(response.data.date);
      }
    } catch (error) {
      // Silently use default rates - this is a public endpoint
      // Do not propagate auth errors from here
      console.log("Using default exchange rates");
    } finally {
      setLoading(false);
    }
  };

  // Convert amount between two explicit currencies (used by CSV exports etc.)
  const convertAmountExplicit = (amount, fromCurrency, toCurrency) => {
    if (!amount || isNaN(amount)) return 0;
    const from = fromCurrency?.toUpperCase() || "UAH";
    const to = toCurrency?.toUpperCase() || "UAH";
    if (from === to) return amount;
    const amountInUAH = from === "UAH" ? amount : amount * (rates[from] || 1);
    return to === "UAH" ? amountInUAH : amountInUAH / (rates[to] || 1);
  };

  // Convert amount from original currency to display currency
  const convertAmount = (amount, originalCurrency) =>
    convertAmountExplicit(amount, originalCurrency, displayCurrency);

  // Get currency symbol
  const getCurrencySymbol = (currency = null) => {
    const curr = currency || displayCurrency;
    switch (curr) {
      case "USD": return "$";
      case "EUR": return "€";
      case "UAH": return "₴";
      default: return "₴";
    }
  };

  // Format amount with currency symbol
  const formatAmount = (amount, originalCurrency = null) => {
    const converted = originalCurrency ? convertAmount(amount, originalCurrency) : (amount || 0);
    const symbol = getCurrencySymbol();
    return `${symbol}${converted.toLocaleString(undefined, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    })}`;
  };

  // Cycle through currencies: UAH -> USD -> EUR -> UAH
  const cycleCurrency = () => {
    setDisplayCurrency(prev => {
      const idx = CURRENCIES.indexOf(prev);
      return CURRENCIES[(idx + 1) % CURRENCIES.length];
    });
  };

  // Get rate for display (e.g., "1 USD = 41.5 UAH")
  const getRateDisplay = () => {
    if (displayCurrency === "UAH") {
      return `1$ = ₴${rates.USD.toFixed(2)}`;
    } else if (displayCurrency === "USD") {
      return `1€ = $${(rates.EUR / rates.USD).toFixed(2)}`;
    } else {
      return `1$ = €${(rates.USD / rates.EUR).toFixed(2)}`;
    }
  };

  return (
    <CurrencyContext.Provider value={{ 
      displayCurrency, 
      setDisplayCurrency,
      rates,
      rateDate,
      loading,
      convertAmount,
      convertAmountExplicit,
      formatAmount,
      getCurrencySymbol,
      cycleCurrency,
      getRateDisplay,
      fetchExchangeRates
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};
